import { Logger } from '@shared/utils/Logger';
import { HTTPClientInterface, HTTPErrorHandler, HTTPRequest } from './HTTPClientInterface';
import { v4 as uuid } from 'uuid';

class HTTPError extends Error {
	statusCode: number;

	constructor(message: string, statusCode: number) {
		super(message);
		this.name = 'HTTPError';
		this.statusCode = statusCode;
		Object.setPrototypeOf(this, HTTPError.prototype);
	}
}

class NetworkError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'NetworkError';
        Object.setPrototypeOf(this, NetworkError.prototype);
    }
}
async function sleep(timeInMilliseconds: number = 2000) {
    await new Promise(resolve => setTimeout(resolve, timeInMilliseconds));
}

export class HTTPClient implements HTTPClientInterface {
    retryCallback?: (request: HTTPRequest, errorCode: number, attemptNumber: number) => Promise<boolean>
    proxyCallback?: (request: HTTPRequest) => Promise<any>
    onError?: HTTPErrorHandler
    clientHeaders: Record<string, string> = {}
    verbose: boolean = false
    instanceID: string

    constructor() {
        this.instanceID = uuid()
        // Logger.log("HTTPClient","Initiated "+this.instanceID)
        
    }

    setClientHeaders(headers: Record<string, string>): void {
        this.clientHeaders = headers
    }

    private failedRequestError(request: HTTPRequest, status: number, data: any): HTTPError {
        try {
            this.onError?.({
                status,
                message: data,
                url: request.url,
                method: request.method,
            })
        } catch (e: any) {
            Logger.logObject("HTTPClient onError handler error", e)
        }
        return new HTTPError(data, status)
    }
	async execute(request: HTTPRequest, retryCount?: number): Promise<any> {
        if (this.proxyCallback) {
            let response = await this.proxyCallback(request)
            if (response) {
                return response
            }
        }

        const method = request.method;

		const headers: Record<string, string> = {
			...(this.clientHeaders ?? {}),	
			...(request.headers ?? {}),	
		};
        if (this.verbose) {
            Logger.logObject(
                method + " " + request.url + " headers:", 
                headers
            )
        }

		let body: BodyInit | undefined = undefined;
		const allowBody = method !== 'GET' && method !== 'HEAD';

		if (allowBody && request.body != null) {
			const isFormData = typeof FormData !== 'undefined' && request.body instanceof FormData;

			if (isFormData || request.body instanceof Blob || request.body instanceof ArrayBuffer) {
                body = request.body as any;
                if (hasHeader(headers, 'content-type')) 
                    deleteHeader(headers, 'content-type');
			} else if (typeof request.body === 'string') {
				body = request.body;
				if (!hasHeader(headers, 'content-type'))
					headers['Content-Type'] = 'text/plain;charset=UTF-8';
			} else {
				body = JSON.stringify(request.body);
				if (!hasHeader(headers, 'content-type'))
					headers['Content-Type'] = 'application/json';
			}
		}

        if (this.verbose) {
            Logger.logObject(
                method + " " + request.url, 
                body
            )
        }

		let res: Response;
		try {
			res = await fetch(request.url, { method, headers, body, signal: request.signal });
		} catch (e: any) {
            Logger.logObject("HTTPClient fetch error", e)
            Logger.logObject("HTTPClient body", body)
			throw new NetworkError(e.message || 'Unknown error');
		}

		const ct = res.headers.get('content-type') || '';

		if (!res.ok) {
			let data: any;
			try {
				if (ct.includes('application/json')) data = JSON.stringify(await res.json());
				else data = await res.text();
			} catch {}
            if (this.retryCallback) {
                let newRetryCount = retryCount ?? 0
                Logger.logCritical(
                    "HTTPClient retry flow",
                    "executing retry attempt " + newRetryCount
                )
                if (newRetryCount === 3) {
        			throw this.failedRequestError(request, res.status, data);
                }
                
                let shouldRetryRequest = await this.retryCallback(
                    request,
                    res.status,
                    newRetryCount
                ) ?? false

                Logger.log(
                    "HTTPClient retry flow",
                    "result " + shouldRetryRequest
                )

                if (!shouldRetryRequest) {
                    Logger.logCritical(
                        "HTTPClient retry flow",
                        "should not retry"
                    )
                    throw this.failedRequestError(request, res.status, data);
                }

                await sleep(
                    500 * newRetryCount
                )

                return await this.execute(request, newRetryCount + 1)

            } else {
    			throw this.failedRequestError(request, res.status, data);
            }
		}

		const rt = request.responseType ?? 'auto';
		if (rt === 'json') return (await res.json());
		if (rt === 'text') return (await res.text()) as unknown;
		if (rt === 'blob') return (await res.blob()) as unknown;
		if (rt === 'arrayBuffer') return (await res.arrayBuffer()) as unknown ;

		// auto by content-type
		if (ct.includes('application/json')) {
            let json = await res.json()
            Logger.logObject(
                "HTTPClient response",
                json
            )

            return json
        }
		if (ct.startsWith('text/')) return (await res.text()) as unknown ;
		return (await res.blob()) as unknown; 
	}
}

function hasHeader(h: Record<string, string>, name: string) {
	const lname = name.toLowerCase();
	return Object.keys(h).some((k) => k.toLowerCase() === lname);
}

function deleteHeader(h: Record<string,string>, name: string) {
  const n = name.toLowerCase();
  for (const k of Object.keys(h)) {
    if (k.toLowerCase() === n) delete h[k];
  }
}
